import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Clock, Leaf, Flame, Plus, Minus, ShoppingCart, X, ChevronLeft, List, LayoutGrid } from 'lucide-react';
import { menuService } from '../services/menuService';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';

// Scroll çubuğunu gizlemek için CSS
const scrollbarHideStyles = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

 const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomerMenuUI = () => {
  const { restaurantId } = useParams();
  const { currentLang, t, ready } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedParentCategory, setSelectedParentCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Ürün listeleme görünümü: 'card' (resimli kart) | 'list' (resimsiz kompakt liste)
  const [viewMode, setViewMode] = useState('card');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Görsel URL'i (relative -> base url eklenir)
  const getImageUrl = (image) =>
    image ? (image.startsWith('http') ? image : `${API_BASE_URL}${image}`) : '/WeOniXIkon.png';

  // Fiyatı tam ve ondalık parçaya böler (ondalık daha küçük punto ile gösterilir)
  const splitPrice = (value) => {
    const num = Number(value) || 0;
    const [intPart, decPart] = num.toFixed(2).split('.');
    return { intPart, decPart };
  };

  // Fiyat etiketi: arkaplanlı pill; ₺ ve ondalık küçük, tam kısım büyük (baseline hizalı)
  const PriceTag = ({ value, size = 'md' }) => {
    const { intPart, decPart } = splitPrice(value);
    const intCls = size === 'lg' ? 'text-2xl' : 'text-lg';
    const curCls = size === 'lg' ? 'text-base' : 'text-sm';
    const decCls = size === 'lg' ? 'text-sm' : 'text-xs';
    const pad = size === 'lg' ? 'px-3 py-1.5' : 'px-2.5 py-1';
    return (
      <span className={`inline-flex items-baseline gap-0.5 rounded-xl ${pad} bg-orange-500/15 ring-1 ring-orange-500/30 text-orange-400 font-extrabold whitespace-nowrap`}>
        <span className={curCls}>₺</span>
        <span className={`${intCls} leading-none`}>{intPart}</span>
        <span className={decCls}>.{decPart}</span>
      </span>
    );
  };

  // Menü verisini seçili dile göre yükler; dil değişince yeniden çağrılır.
  useEffect(() => {
    // Dil çözümlemesi tamamlanmadan yükleme yapma (yanlış dille çift fetch'i önler).
    if (!ready) return;

    const loadMenu = async () => {
      setLoading(true);
      try {
        // Load restaurant
        const restaurantData = await menuService.getRestaurant(restaurantId, currentLang);
        setRestaurant(restaurantData);

        // Load categories
        const categoriesData = await menuService.getCategories(restaurantId, currentLang);
        setCategories(categoriesData.map(cat => ({
          id: cat.id?.toString() || cat.id,
          name: cat.name,
          icon: cat.icon || '',
          color: cat.color || 'bg-gray-100',
          parentCategoryId: (cat.parentCategoryId || cat.ParentCategoryId)
            ? String(cat.parentCategoryId || cat.ParentCategoryId)
            : null
        })));

        // Load products
        const productsData = await menuService.getProducts(restaurantId, currentLang);
        setProducts(productsData.map(product => ({
          id: product.id?.toString() || product.id,
          categoryId: product.categoryId?.toString() || product.categoryId,
          name: product.name,
          description: product.description,
          price: product.price,
          image: product.image,
          isVegetarian: product.isVegetarian || false,
          isVegan: product.isVegan || false,
          isGlutenFree: product.isGlutenFree || false,
          isSpicy: product.isSpicy || false,
          spicyLevel: product.spicyLevel || 0,
          isFeatured: product.isFeatured || false,
          preparationTime: product.preparationTime,
          discountedPrice: product.discountedPrice,
          ingredients: product.ingredients || '',
          englishIngredients: product.englishIngredients || '',
          calories: product.calories || 0
        })));
      } catch (error) {
        console.error("Error loading menu:", error);
      } finally {
        setLoading(false);
      }
    };

    loadMenu();
  }, [restaurantId, currentLang, ready]);

  // Alt kategori hiyerarşisi: parentCategoryId'si olmayanlar ana, olanlar alt kategori.
  const parentCategories = categories.filter(c => !c.parentCategoryId);
  const childCategories = categories.filter(c => c.parentCategoryId);
  const hasHierarchy = childCategories.length > 0;
  const childCategoriesOfSelected = selectedParentCategory
    ? categories.filter(c => String(c.parentCategoryId) === String(selectedParentCategory))
    : [];

  const filteredProducts = products.filter(product => {
    let matchesCategory = true;
    if (selectedCategory) {
      // Belirli bir kategori (ana ya da alt) seçili
      matchesCategory = product.categoryId === selectedCategory;
    } else if (selectedParentCategory) {
      // Ana kategori seçili, alt seçilmemiş: ana + tüm alt kategorilerinin ürünleri
      const childIds = childCategoriesOfSelected.map(c => String(c.id));
      matchesCategory = product.categoryId === selectedParentCategory || childIds.includes(product.categoryId);
    }
    const matchesSearch = !searchTerm ||
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === productId);
      if (existing && existing.quantity > 1) {
        return prev.map(item => 
          item.id === productId 
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }
      return prev.filter(item => item.id !== productId);
    });
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Font Awesome class'larını emoji'ye çevir
  const getCategoryIcon = (iconClass) => {
    if (!iconClass) return null;

    const iconMap = {
      'fa-pizza-slice': '🍕',
      'fa-hamburger': '🍔',
      'fa-leaf': '🥗',
      'fa-utensils': '🍜',
      'fa-fish': '🍣',
      'fa-drumstick-bite': '🥩',
      'fa-bread-slice': '🥪',
      'fa-taco': '🌮',
      'fa-french-fries': '🍟',
      'fa-cake-candles': '🍰',
      'fa-ice-cream': '🍦',
      'fa-mug-hot': '☕',
      'fa-beer-mug-empty': '🍺',
      'fa-wine-glass': '🍷',
      'fa-bottle-water': '🥤',
      'fa-apple-whole': '🧃',
      'fa-smoking': '💨',
      'fa-cookie-bite': '🍪',
      'fa-circle': '🍩',
      'fa-candy-cane': '🍫',
      'fa-seedling': '🥜',
      'fa-carrot': '🥕',
      'fa-cheese': '🧀',
      'fa-egg': '🥚',
      'fa-square': '🧇',
      'fa-bacon': '🥓',
      'fa-hotdog': '🌭',
      'fa-glass-water': '🥤',
      'fa-martini-glass': '🍸',
      'fa-martini-glass-citrus': '🍸',
      'fa-wine-bottle': '🍾',
      'fa-champagne-glasses': '🥂',
      'fa-whiskey-glass': '🥃',
      'fa-mug-saucer': '☕',
      'fa-bowl-food': '🍲',
      'fa-plate-wheat': '🍝',
      'fa-shrimp': '🍤',
      'fa-lemon': '🍋',
      'fa-pepper-hot': '🌶️'
    };

    if (iconMap[iconClass]) return iconMap[iconClass];
    // Bilinmeyen Font Awesome kodu (fa-...) ise ham metin gösterme, varsayılan ikon dön
    if (typeof iconClass === 'string' && iconClass.startsWith('fa-')) return '🍽️';
    return iconClass;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-white">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // Ürün detay sayfası
  if (selectedProduct) {
    const p = selectedProduct;
    // Alerjenler: İngilizce seçiliyse EnglishIngredients kolonu, yoksa temel ingredients
    const allergenText = (currentLang === 'en' && p.englishIngredients) ? p.englishIngredients : p.ingredients;
    return (
      <div className="min-h-screen bg-gray-900 text-white pb-8">
        <style>{scrollbarHideStyles}</style>
        {/* Büyük görsel + geri butonu */}
        <div className="relative">
          <button
            onClick={() => setSelectedProduct(null)}
            className="absolute top-4 left-4 z-10 flex items-center gap-1 bg-black/60 hover:bg-black/80 text-white rounded-full pl-2 pr-3 py-2 backdrop-blur transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="text-sm font-medium">{t('back')}</span>
          </button>
          <div className="w-full h-72 sm:h-96 bg-gray-800 flex items-center justify-center overflow-hidden">
            <img
              src={getImageUrl(p.image)}
              alt={p.name}
              className={p.image ? 'max-w-full max-h-full w-auto h-auto object-contain' : 'w-24 h-24 object-contain opacity-40'}
              onError={(e) => { e.target.src = '/WeOniXIkon.png'; e.target.className = 'w-24 h-24 object-contain opacity-40'; }}
            />
          </div>
        </div>

        {/* Detaylar */}
        <div className="max-w-2xl mx-auto p-5">
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl font-bold">{p.name}</h1>
            <PriceTag value={p.price} size="lg" />
          </div>

          {false && (p.isVegetarian || p.isVegan || p.isGlutenFree || p.isSpicy) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {p.isVegetarian && (
                <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 px-2.5 py-1 rounded-full text-xs">
                  <Leaf className="h-3 w-3" /> {t('vegetarian')}
                </span>
              )}
              {p.isVegan && (
                <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-400 px-2.5 py-1 rounded-full text-xs">
                  <Leaf className="h-3 w-3" /> Vegan
                </span>
              )}
              {p.isGlutenFree && (
                <span className="inline-flex items-center gap-1 bg-amber-500/20 text-amber-400 px-2.5 py-1 rounded-full text-xs">
                  Glütensiz
                </span>
              )}
              {p.isSpicy && (
                <span className="inline-flex items-center gap-1 bg-red-500/20 text-red-400 px-2.5 py-1 rounded-full text-xs">
                  <Flame className="h-3 w-3" /> {t('spicy')}{p.spicyLevel > 0 ? ` ${p.spicyLevel}/5` : ''}
                </span>
              )}
            </div>
          )}

          {p.description && (
            <p className="text-gray-300 mt-4 leading-relaxed">{p.description}</p>
          )}

          {/* Tanımlar */}
          <div className="mt-6 divide-y divide-gray-800 border-y border-gray-800">
            {allergenText && (
              <div className="py-3">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{t('ingredients')}</div>
                <div className="text-sm text-gray-200">{allergenText}</div>
              </div>
            )}
            {p.calories > 0 && (
              <div className="py-3 flex items-center justify-between">
                <span className="text-sm text-gray-400">{t('calories')}</span>
                <span className="text-sm text-gray-200">{p.calories} {t('caloriesUnit')}</span>
              </div>
            )}
            {p.preparationTime ? (
              <div className="py-3 flex items-center justify-between">
                <span className="text-sm text-gray-400 flex items-center gap-1"><Clock className="h-4 w-4" /> {t('prepTime')}</span>
                <span className="text-sm text-gray-200">{p.preparationTime} {t('minutesShort')}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <style>{scrollbarHideStyles}</style>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-900 border-b border-gray-700">
        <div className="p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-3 min-w-0">
              <div className="h-28 w-28 flex-shrink-0 bg-gray-800 rounded-lg flex items-center justify-center overflow-hidden">
                <img
                  src={restaurant.logo ? (restaurant.logo.startsWith('http') ? restaurant.logo : `${API_BASE_URL}${restaurant.logo}`) : '/WeOniXIkon.png'}
                  alt="Restaurant Logo"
                  className={restaurant.logo ? 'w-full h-full object-contain' : 'w-16 h-16 object-contain opacity-50'}
                  onError={(e) => {
                    e.target.src = '/WeOniXIkon.png';
                    e.target.className = 'w-16 h-16 object-contain opacity-50';
                  }}
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold truncate">{restaurant.name}</h1>
                <p className="text-gray-400 text-sm line-clamp-2">{restaurant.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              {/* Görünüm değiştirme (kart / liste) */}
              <button
                onClick={() => setViewMode(prev => (prev === 'card' ? 'list' : 'card'))}
                title={viewMode === 'card' ? 'Liste görünümü' : 'Kart görünümü'}
                className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1.5 rounded-lg text-sm transition-colors"
              >
                {viewMode === 'card' ? <List className="h-4 w-4" /> : <LayoutGrid className="h-4 w-4" />}
                <span className="whitespace-nowrap">{viewMode === 'card' ? 'Liste' : 'Kart'}</span>
              </button>
              {/* Dil seçici (yalnızca birden çok dil varsa görünür) */}
              <LanguageSelector />
              {cart.length > 0 && (
                <button
                  onClick={() => setShowCart(true)}
                  className="relative bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                >
                  <ShoppingCart className="h-4 w-4" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
                    {getCartItemCount()}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-800 text-white pl-10 pr-4 py-1.5 rounded-xl border border-gray-700 focus:border-orange-500 focus:outline-none"
            />
            <button className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-2 rounded-lg">
              <Search className="h-4 w-4" />
            </button>
          </div>

          {/* Category Filter */}
          {!hasHierarchy ? (
            /* Alt kategori yoksa: mevcut tek seviyeli çubuk */
            <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setSelectedCategory('')}
                className={`flex-shrink-0 flex items-center space-x-1.5 px-3 py-1 text-xs sm:space-x-2 sm:px-4 sm:py-2 sm:text-base rounded-xl transition-colors ${
                  selectedCategory === ''
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span>🍽️</span>
                <span className="whitespace-nowrap">{t('all')}</span>
              </button>
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex-shrink-0 flex items-center space-x-1.5 px-3 py-1 text-xs sm:space-x-2 sm:px-4 sm:py-2 sm:text-base rounded-xl transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {category.icon && <span>{getCategoryIcon(category.icon)}</span>}
                  <span className="whitespace-nowrap">{category.name}</span>
                </button>
              ))}
            </div>
          ) : (
            /* Alt kategori varsa: iki seviyeli çubuk */
            <div className="space-y-2">
              {/* Üst (ana) kategori çubuğu */}
              <div className="flex space-x-3 overflow-x-auto pb-1 scrollbar-hide">
                <button
                  onClick={() => { setSelectedParentCategory(''); setSelectedCategory(''); }}
                  className={`flex-shrink-0 flex items-center space-x-1.5 px-3 py-1 text-xs sm:space-x-2 sm:px-4 sm:py-2 sm:text-base rounded-xl transition-colors ${
                    selectedParentCategory === ''
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <span>🍽️</span>
                  <span className="whitespace-nowrap">{t('all')}</span>
                </button>
                {parentCategories.map(parent => (
                  <button
                    key={parent.id}
                    onClick={() => { setSelectedParentCategory(parent.id); setSelectedCategory(''); }}
                    className={`flex-shrink-0 flex items-center space-x-1.5 px-3 py-1 text-xs sm:space-x-2 sm:px-4 sm:py-2 sm:text-base rounded-xl transition-colors ${
                      selectedParentCategory === parent.id
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {parent.icon && <span>{getCategoryIcon(parent.icon)}</span>}
                    <span className="whitespace-nowrap">{parent.name}</span>
                  </button>
                ))}
              </div>

              {/* Alt kategori çubuğu (bir ana kategori seçiliyse ve alt kategorisi varsa) */}
              {selectedParentCategory && childCategoriesOfSelected.length > 0 && (
                <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`flex-shrink-0 flex items-center space-x-1.5 px-3 py-1 text-xs sm:space-x-2 sm:px-4 sm:py-2 sm:text-base rounded-lg transition-colors ${
                      selectedCategory === ''
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <span className="whitespace-nowrap">{t('all')}</span>
                  </button>
                  {childCategoriesOfSelected.map(child => (
                    <button
                      key={child.id}
                      onClick={() => setSelectedCategory(child.id)}
                      className={`flex-shrink-0 flex items-center space-x-1.5 px-3 py-1 text-xs sm:space-x-2 sm:px-4 sm:py-2 sm:text-base rounded-lg transition-colors ${
                        selectedCategory === child.id
                          ? 'bg-orange-500 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      {child.icon && <span>{getCategoryIcon(child.icon)}</span>}
                      <span className="whitespace-nowrap">{child.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Products List */}
      <div className={`p-4 max-w-2xl mx-auto ${viewMode === 'list' ? 'space-y-1.5' : 'space-y-3'}`}>
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🔍</div>
            <h3 className="text-lg font-medium text-gray-300 mb-2">{t('noProducts')}</h3>
            <p className="text-gray-500">{t('noProductsHint')}</p>
          </div>
        ) : viewMode === 'list' ? (
          /* Liste görünümü: resimsiz — ürün adı, içerik ve fiyat (daha fazla ürün görünür) */
          filteredProducts.map(product => (
            <button
              key={product.id}
              onClick={() => setSelectedProduct(product)}
              className="w-full text-left bg-gray-800 hover:bg-gray-700/70 rounded-lg px-3 py-1.5 flex items-center gap-3 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <h3 className="text-sm font-semibold text-white truncate">{product.name}</h3>
                  {false && product.isVegetarian && (
                    <span className="bg-green-500/20 text-green-400 p-0.5 rounded-full flex-shrink-0"><Leaf className="h-3 w-3" /></span>
                  )}
                  {false && product.isSpicy && (
                    <span className="bg-red-500/20 text-red-400 p-0.5 rounded-full flex-shrink-0"><Flame className="h-3 w-3" /></span>
                  )}
                </div>
                {(product.ingredients || product.description) && (
                  <p className="text-gray-400 text-xs truncate mt-0.5">{product.ingredients || product.description}</p>
                )}
              </div>
              <div className="flex-shrink-0">
                <PriceTag value={product.price} />
              </div>
            </button>
          ))
        ) : (
          filteredProducts.map(product => (
            <button
              key={product.id}
              onClick={() => setSelectedProduct(product)}
              className="w-full text-left bg-gray-800 hover:bg-gray-700/70 rounded-2xl overflow-hidden flex items-stretch transition-colors min-h-[96px]"
            >
              {/* Görsel (solda, kartı tam dolduracak) */}
              <div className="w-24 sm:w-28 flex-shrink-0 self-stretch bg-gray-700 flex items-center justify-center overflow-hidden">
                <img
                  src={getImageUrl(product.image)}
                  alt={product.name}
                  className={product.image ? 'w-full h-full object-cover' : 'w-12 h-12 object-contain opacity-40'}
                  onError={(e) => { e.target.src = '/WeOniXIkon.png'; e.target.className = 'w-12 h-12 object-contain opacity-40'; }}
                />
              </div>

              {/* Orta: isim + kısa açıklama */}
              <div className="flex-1 min-w-0 py-3 pl-3 flex flex-col justify-center">
                <div className="flex items-center gap-1.5 min-w-0">
                  <h3 className="text-lg font-bold text-white truncate">{product.name}</h3>
                  {false && product.isVegetarian && (
                    <span className="bg-green-500/20 text-green-400 p-0.5 rounded-full flex-shrink-0"><Leaf className="h-3 w-3" /></span>
                  )}
                  {false && product.isSpicy && (
                    <span className="bg-red-500/20 text-red-400 p-0.5 rounded-full flex-shrink-0"><Flame className="h-3 w-3" /></span>
                  )}
                </div>
                {product.description && (
                  <p
                    className="text-gray-400 text-xs sm:text-sm mt-1"
                    style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {product.description}
                  </p>
                )}
              </div>

              {/* Sağ: fiyat */}
              <div className="flex items-center px-3 flex-shrink-0">
                <PriceTag value={product.price} />
              </div>
            </button>
          ))
        )}
      </div>

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
          <div className="bg-gray-800 w-full max-h-[80vh] rounded-t-2xl">
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">{t('cart')}</h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-96">
              {cart.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">🛒</div>
                  <p className="text-gray-400">{t('cartEmpty')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <img 
                          src={`${API_BASE_URL}${item.image}`} 
                          alt={item.name} 
                          className="w-12 h-12 rounded-lg object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div>
                          <h4 className="font-medium">{item.name}</h4>
                          <p className="text-orange-400">₺{item.price}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="font-medium">{item.quantity}</span>
                        {/* 
                        <button
                          onClick={() => addToCart(item)}
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        */}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {cart.length > 0 && (
              <div className="p-4 border-t border-gray-700">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold">{t('total')}</span>
                  <span className="text-xl font-bold text-orange-400">₺{getCartTotal()}</span>
                </div>
                <button className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-medium transition-colors">
                  {t('placeOrder')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Cart Button */}
      {cart.length > 0 && !showCart && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 bg-orange-600 text-white p-4 rounded-full shadow-lg flex items-center space-x-2 z-40"
        >
          <ShoppingCart className="h-5 w-5" />
          <span className="font-medium">₺{getCartTotal()}</span>
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
            {getCartItemCount()}
          </span>
        </button>
      )}
    </div>
  );
};

export default CustomerMenuUI;